import type { DeliveryJob, RouteLeg } from '@/api/types';
import { isInHand, isReadyToCollect } from './status';

/**
 * Turning a list of jobs into a round.
 *
 * Two things a rider does that a single-job screen cannot help with: collecting
 * several bags at one counter, and deciding which door to go to next. Both are
 * about the relationship BETWEEN jobs, so both live here rather than inside a
 * card that only knows about itself.
 */

export type Point = { latitude: number; longitude: number };

/**
 * Parses the decimal strings the API sends, once, at the point of use.
 *
 * Coordinates cross the wire as strings for the same reason money does — the
 * database holds them at a fixed scale and a float round-trip is lossy. Unlike
 * money, they ARE arithmetic here, so they are parsed deliberately rather than
 * by accident.
 */
export function toPoint(
  latitude: string | null | undefined,
  longitude: string | null | undefined,
): Point | null {
  if (!latitude || !longitude) return null;
  const lat = Number.parseFloat(latitude);
  const lng = Number.parseFloat(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { latitude: lat, longitude: lng };
}

export function dropPoint(job: DeliveryJob): Point | null {
  return toPoint(job.dropLatitude, job.dropLongitude);
}

/**
 * Straight-line distance in kilometres.
 *
 * Haversine, not road distance, and the UI says so: "nearest first" here means
 * as the crow flies, which on a river or a one-way system is not the same as
 * nearest to ride to. Getting the real answer means a routing API, a key and a
 * per-request bill — worth doing, but it should be a decision somebody makes
 * rather than something that quietly appears on the invoice.
 *
 * It is still a large improvement on the alternative, which is the order the
 * dispatcher happened to assign them in.
 */
export function distanceKm(from: Point, to: Point): number {
  const EARTH_RADIUS_KM = 6371;
  const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

  const dLat = toRadians(to.latitude - from.latitude);
  const dLng = toRadians(to.longitude - from.longitude);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.latitude)) *
      Math.cos(toRadians(to.latitude)) *
      Math.sin(dLng / 2) ** 2;

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(a)));
}

export function formatDistance(km: number | null | undefined): string {
  if (km === null || km === undefined || !Number.isFinite(km)) return '';
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

/*
 * The same two constants as the backend's `engines/routing` — keep them in
 * step. Streets run ~30% longer than the crow flies; 20 km/h is an average
 * city ride, stops and signals included.
 */
const ROAD_FACTOR = 1.3;
const AVERAGE_SPEED_KMH = 20;

/**
 * The leg for a job: the server's figure when it sent one, otherwise the same
 * estimate worked out here.
 *
 * The fallback only ever fires once the bag is in hand, because that is the
 * only time this app holds the customer's coordinates. It exists for a server
 * that predates `route` — the answer is the same arithmetic either way, so the
 * rider cannot tell which side did it.
 */
export function legFor(
  route: RouteLeg | null | undefined,
  from: Point | null,
  to: Point | null,
): RouteLeg | null {
  if (route) return route;
  if (!from || !to) return null;
  const roadKm = distanceKm(from, to) * ROAD_FACTOR;
  return {
    distanceMeters: Math.round(roadKm * 1000),
    durationSeconds: Math.round((roadKm / AVERAGE_SPEED_KMH) * 3600),
    source: 'ESTIMATE',
  };
}

/** `legFor` for a run-sheet row. */
export function jobLeg(job: DeliveryJob): RouteLeg | null {
  return legFor(job.route, toPoint(job.pharmacyLatitude, job.pharmacyLongitude), dropPoint(job));
}

/**
 * "3.2 km" / "800 m" for an estimated leg.
 *
 * Short distances are rounded to 50 m: "about 810 m" claims a precision the
 * estimate does not have.
 */
export function formatLegDistance(leg: RouteLeg | null | undefined): string {
  if (!leg) return '';
  if (leg.distanceMeters < 1000) return `${Math.max(50, Math.round(leg.distanceMeters / 50) * 50)} m`;
  return formatDistance(leg.distanceMeters / 1000);
}

/**
 * "about 14 min" / "about 1 hr 5 min".
 *
 * "About" every time, and rounded up to the minute: it is an estimate at an
 * average speed with no traffic in it, and a rider promised 13 who takes 16
 * has been told something untrue.
 */
export function formatLegDuration(leg: RouteLeg | null | undefined): string {
  const time = formatLegTime(leg);
  return time ? `about ${time}` : '';
}

/** "14 min" / "1 hr 5 min", for when an "about" is already on the line. */
export function formatLegTime(leg: RouteLeg | null | undefined): string {
  if (!leg) return '';
  const minutes = Math.max(1, Math.ceil(leg.durationSeconds / 60));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

/**
 * The bags in hand, nearest door first.
 *
 * Only the jobs actually being CARRIED are ordered this way, and only when the
 * rider's own position is known. A job not yet picked up has no coordinates on
 * the wire at all — the backend withholds the destination until the medicine
 * changes hands — so there is nothing to sort it by, and inventing an order
 * for it would be inventing an order.
 *
 * Returns `null` when there is nothing to say, so the caller can leave the
 * sheet in its normal stage order rather than showing an empty "route".
 */
export function nearestFirst(
  jobs: readonly DeliveryJob[],
  origin: Point | null,
): Array<{ job: DeliveryJob; km: number }> | null {
  if (!origin) return null;

  const withDistance = jobs
    .filter((job) => isInHand(job.status))
    .map((job) => {
      const point = dropPoint(job);
      return point ? { job, km: distanceKm(origin, point) } : null;
    })
    .filter((entry): entry is { job: DeliveryJob; km: number } => entry !== null);

  // One drop is not a route, and neither is none.
  if (withDistance.length < 2) return null;

  return withDistance.sort((a, b) => a.km - b.km);
}

/** Every job waiting at one shop — one stop, however many orders. */
export type ShopStop = {
  pharmacyId: string;
  pharmacyName: string;
  jobs: DeliveryJob[];
  /** How many of them are packed and on the counter now. */
  ready: number;
};

/**
 * The run sheet as three piles, in the order they get worked.
 *
 *  - `deliver`: bags already in hand. Nearest door first when the phone knows
 *    where it is — and `ordered` says whether that happened, so the screen
 *    can say "nearest first" only when it is true.
 *  - `collect`: jobs heading to, or standing at, a shop — grouped BY shop,
 *    because three orders at one counter are one stop, not three.
 *  - `accept`: new, not yet accepted.
 *
 * Built from the sheet already in working order, so everything inside a pile
 * keeps that order unless there is a better one.
 */
export function planRunSheet(
  jobs: readonly DeliveryJob[],
  origin: Point | null,
): {
  deliver: Array<{ job: DeliveryJob; km: number | null }>;
  ordered: boolean;
  collect: ShopStop[];
  accept: DeliveryJob[];
} {
  const inHand = jobs.filter((job) => isInHand(job.status));
  const nearest = nearestFirst(inHand, origin);
  const deliver = nearest
    ? [
        ...nearest,
        // Anything without a pin keeps its place at the end rather than vanishing.
        ...inHand
          .filter((job) => !nearest.some((entry) => entry.job.id === job.id))
          .map((job) => ({ job, km: null })),
      ]
    : inHand.map((job) => ({ job, km: null }));

  const stops = new Map<string, ShopStop>();
  for (const job of jobs) {
    if (job.status !== 'ACCEPTED' && job.status !== 'ARRIVED_AT_PHARMACY') continue;
    const key = job.pharmacyId ?? `job:${job.id}`;
    const stop = stops.get(key) ?? {
      pharmacyId: key,
      pharmacyName: job.pharmacyName ?? 'Pharmacy',
      jobs: [],
      ready: 0,
    };
    stop.jobs.push(job);
    if (isReadyToCollect(job)) stop.ready += 1;
    stops.set(key, stop);
  }

  return {
    deliver,
    ordered: nearest !== null,
    collect: [...stops.values()],
    accept: jobs.filter((job) => job.status === 'ASSIGNED'),
  };
}

/**
 * The bags waiting at one counter.
 *
 * What makes a batch pickup worth offering: the rider is standing at a shop
 * and more than one of their jobs is packed and waiting there. Keyed on the
 * pharmacy ID rather than its name — see `DeliveryJob.pharmacyId`.
 *
 * Only jobs that are actually collectable are counted. Offering "collect all
 * 3" when one of the three is still being packed produces a batch that comes
 * back with a refusal in it, which is worse than not offering the number.
 */
export function readyAtSamePharmacy(
  jobs: readonly DeliveryJob[],
  pharmacyId: string | null | undefined,
): DeliveryJob[] {
  if (!pharmacyId) return [];
  return jobs.filter(
    (job) =>
      job.pharmacyId === pharmacyId &&
      job.status === 'ARRIVED_AT_PHARMACY' &&
      isReadyToCollect(job),
  );
}
